/**
 * @Author: Klaudie <klaudie@foxprofi.cz>
 */

import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { IDLE_DIALOG_TIMEOUT } from '@/lib/constants'

interface IdleWarningDialogProps {
  open: boolean
  onStayLoggedIn: () => void
  onLogout: () => void
}

/**
 * Dialog zobrazený při nečinnosti uživatele
 * Zobrazí countdown timer a umožní uživateli zůstat přihlášen nebo se odhlásit
 */
export function IdleWarningDialog({ open, onStayLoggedIn, onLogout }: IdleWarningDialogProps) {
  const [remainingTime, setRemainingTime] = useState(IDLE_DIALOG_TIMEOUT)

  useEffect(() => {
    if (!open) {
      setRemainingTime(IDLE_DIALOG_TIMEOUT)
      return
    }

    const startTime = Date.now()
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime
      const remaining = Math.max(0, IDLE_DIALOG_TIMEOUT - elapsed)
      
      setRemainingTime(remaining)

      // Automatické odhlášení po vypršení času
      if (remaining === 0) {
        clearInterval(interval)
        onLogout()
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [open, onLogout])

  const formatTime = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-[425px]" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Jste tu ještě?</DialogTitle>
          <DialogDescription>
            Z důvodu nečinnosti budete za <strong>{formatTime(remainingTime)}</strong> automaticky
            odhlášeni.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onLogout}>
            Odhlásit se
          </Button>
          <Button onClick={onStayLoggedIn}>Zůstat přihlášen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
